import { Money } from '@libs/money/money.interfaces';
import {
    BaggageWarningInteraction,
    BaggageWarningPosition,
    SembonusView,
    StatsigEvent,
} from '@/tracking/statsig/types';
import { eventIsTracked } from '@/tracking/statsig/utils';
import { pushStatsig } from '@/tracking/statsig/index';
import { AnyProduct } from '@shared/interfaces/product';
import {
    ActivityProduct,
    AddonProduct,
    Cart,
    FlightProduct,
    HotelProduct,
    ProductType,
    SeatingProduct,
    ServicePackageType,
    TransferProduct,
} from '@/api/Cart/cart.interfaces';
import { Baggage } from '@shared/interfaces/baggage';
import {
    cartProductHasRyanair,
    isActivityOnly,
    isFerryOnly,
    isFlightOnly,
    isHotelOnly,
} from '@/helpers/cart';
import { getBaggageSelectedCount } from '@/components/shared/PriceBreakdown/map/flight';
import { sum, uniq } from 'lodash';
import { getAllSeatsForFlightProduct } from '@/helpers/seating';
import { TransferOption } from '@/api/Transfer/transfer.interfaces';
import { TransferTrip } from '@/helpers/transfer/transfer.interfaces';
import { ServicePackageName } from '@ui/components/ServicePackage/ServicePackage.interfaces';
import { cleanSectionIds } from '@/helpers/validation';
import { isTransferProduct } from '@/components/checkoutPageV3/Sections/Addons/Transfer/Trips/Trip/Shared/TransferDescription/TransferDescription.helpers';

const getProductCountByType = (productArray: AnyProduct[]): Record<string, number> => {
    return productArray.reduce(
        (acc, product) => {
            const type = product.$type;
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        },
        {} as Record<string, number>
    );
};

const getProductsType = (productArray: AnyProduct[]): Record<string, number> => {
    return productArray.reduce(
        (acc, product) => {
            const type =
                product.Type === ProductType.Addon
                    ? `count_${product.$type?.toLowerCase()}`
                    : `count_${product.Type?.toLowerCase()}`;

            acc[type] = (acc[type] || 0) + 1;
            return acc;
        },
        {} as Record<string, number>
    );
};

const getProductsRyanair = (flights: FlightProduct[]) => {
    return {
        count_flight_ryanair: flights.filter((p) => cartProductHasRyanair(p)).length,
    };
};

const getProductsRooms = (products: HotelProduct[]) =>
    products.reduce(
        (rooms, product) => {
            rooms.count_rooms += product.Rooms?.length || 0;
            return rooms;
        },
        { count_rooms: 0 }
    );

const getProductsTickets = (products: ActivityProduct[]) =>
    products.reduce(
        (rooms, product) => {
            rooms.count_tickets += product.Tickets?.length || 0;
            return rooms;
        },
        { count_tickets: 0 }
    );

export const logStatsigCheckoutInitiated = (
    cart: Cart,
    totalToPay: Money,
    isLoggedIn: boolean
) => {
    if (!eventIsTracked(StatsigEvent.CHECKOUT_PAGE_INITIATED)) {
        const metadata = {
            ...getProductsMetaData(cart),
            currency: totalToPay.Currency,
            value: totalToPay.Amount.toString(),
            isLoggedIn,
        };

        pushStatsig({
            eventName: StatsigEvent.CHECKOUT_PAGE_INITIATED,
            metadata,
        });
    }
};

export const logStatsigCheckoutRyanairInteraction = (interactionType: 'BookingProcess') => {
    const metadata = {
        interactionType,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_RYANAIR_INTERACTION,
        metadata,
    });
};

export const logStatsigCustomerDetailsInitiated = (totalToPay: Money) => {
    if (!eventIsTracked(StatsigEvent.CHECKOUT_USER_DETAILS_INITIATED)) {
        const metadata = {
            currency: totalToPay.Currency,
            value: totalToPay.Amount.toString(),
        };

        pushStatsig({
            eventName: StatsigEvent.CHECKOUT_USER_DETAILS_INITIATED,
            metadata,
        });
    }
};

export const logStatsigCustomerDetailsCompleted = (cart: Cart, totalToPay: Money) => {
    if (!eventIsTracked(StatsigEvent.CHECKOUT_USER_DETAILS_COMPLETED)) {
        const metadata = {
            ...getProductsMetaData(cart),
            currency: totalToPay.Currency,
            value: totalToPay.Amount,
        };

        pushStatsig({
            eventName: StatsigEvent.CHECKOUT_USER_DETAILS_COMPLETED,
            metadata,
        });
    }
};

export const logStatsigCustomerOptOutNewsletter = () => {
    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_USER_OPT_OUT_NEWSLETTER,
    });
};

export const logStatsigCheckoutAddRemoveAddon = (
    product: AddonProduct,
    event: StatsigEvent.CHECKOUT_ADD_ADDON | StatsigEvent.CHECKOUT_REMOVE_ADDON
) => {
    let servicePackageTier: ServicePackageName | undefined;

    if ('Ordinal' in product && product.Ordinal !== undefined) {
        switch (product.Ordinal) {
            case ServicePackageType.None:
                servicePackageTier = ServicePackageName.None;
                break;
            case ServicePackageType.Basic:
                servicePackageTier = ServicePackageName.Basic;
                break;
            case ServicePackageType.Premium:
                servicePackageTier = ServicePackageName.Premium;
                break;
            default:
                servicePackageTier = undefined;
                break;
        }
    }

    const metadata = {
        type: product.$type,
        tier: servicePackageTier,
    };

    pushStatsig({
        eventName: event,
        metadata,
    });
};

const logStatsigAddRemoveGordianSeating = (
    seats: SeatingProduct[],
    flightProduct: FlightProduct,
    event: StatsigEvent.CHECKOUT_ADD_SEATING | StatsigEvent.CHECKOUT_REMOVE_SEATING
) => {
    const metadata = {
        type: flightProduct.$type,
        seats: seats.map((s) => ({
            place: [s?.Row, s?.Column].join(''),
            description: s?.Description,
        })),
    };

    pushStatsig({
        eventName: event,
        metadata,
    });
};

export const logStatsigGordianSeatingChanged = (
    flightProduct: FlightProduct,
    seats: SeatingProduct[],
    prevSeats: SeatingProduct[]
) => {
    const removedSeats = prevSeats.filter((seat) => !seats.some(({ Id }) => Id === seat.Id));
    const addedSeats = seats.filter((seat) => !prevSeats.some(({ Id }) => Id === seat.Id));
    if (removedSeats.length) {
        logStatsigAddRemoveGordianSeating(
            seats,
            flightProduct,
            StatsigEvent.CHECKOUT_REMOVE_SEATING
        );
    }
    if (addedSeats.length) {
        logStatsigAddRemoveGordianSeating(
            seats,
            flightProduct,
            StatsigEvent.CHECKOUT_ADD_SEATING
        );
    }
};

export const logStatsigAddRemoveBaggage = (
    baggage: Baggage,
    flightProduct: FlightProduct,
    event: StatsigEvent.CHECKOUT_ADD_BAGGAGE | StatsigEvent.CHECKOUT_REMOVE_BAGGAGE
) => {
    const metadata = {
        type: flightProduct.$type,
        baggage: {
            type: baggage?.Type,
            description: baggage?.Description,
            pieces: baggage?.Pieces,
            weight: baggage?.Weight,
        },
    };

    pushStatsig({
        eventName: event,
        metadata,
    });
};

export const logStatsigCheckoutRemoveProduct = (product: AnyProduct) => {
    const metadata = {
        type: product.$type,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_REMOVE_PRODUCT,
        metadata,
    });
};

export const logStatsigCheckoutCancellationPanelOpen = (type: ProductType) => {
    const metadata = {
        type,
    };
    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_CANCELLATION_PANEL_OPEN,
        metadata,
    });
};

export const logStatsigCheckoutPriceBreakdownBadgeOpen = (
    type: ProductType.Activity | ProductType.Hotel
) => {
    const metadata = {
        type,
    };
    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_PRICE_BREAKDOWN_BADGE_OPEN,
        metadata,
    });
};

export const logStatsigCheckoutShowPriceBreakdownDetails = (type: string) => {
    const metadata = {
        type,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_PRICE_BREAKDOWN_DETAILS_OPEN,
        metadata,
    });
};

export const logStatsigCheckoutImportantNotesOpen = () => {
    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_IMPORTANT_NOTES_OPEN,
    });
};

export const logStatsigCheckoutSubmitInvalidState = (
    visible: number,
    invalid: number,
    invalidSections?: string[]
) => {
    const metadata: any = {
        count_visible_sections: visible,
        count_invalid_sections: invalid,
    };

    if (invalidSections && invalidSections.length > 0) {
        const cleanedSections = cleanSectionIds(invalidSections);
        metadata.invalid_sections = cleanedSections;
        metadata.invalid_sections_string = cleanedSections.join(',');
    }

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_SUBMIT_INVALID_STATE,
        metadata,
    });
};

export const logStatsigMetaFlightInitiated = () => {
    if (!eventIsTracked(StatsigEvent.META_FLIGHT_PAGE_INITIATED)) {
        pushStatsig({
            eventName: StatsigEvent.META_FLIGHT_PAGE_INITIATED,
        });
    }
};

export const logStatsigCheckoutMoreInformationOpen = (type: string) => {
    const metadata = {
        type,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_MORE_INFORMATION_OPEN,
        metadata,
    });
};

export const logStatsigCheckoutPriceDetailsOpen = (type: string) => {
    const metadata = {
        type,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_PRICE_DETAILS_OPEN,
        metadata,
    });
};

export const logStatsigCheckoutTransferBanner = (cart: Cart) => {
    const metadata = {
        ...getProductsMetaData(cart),
    };

    if (!eventIsTracked(StatsigEvent.CHECKOUT_TRANSFER_BANNER)) {
        pushStatsig({
            eventName: StatsigEvent.CHECKOUT_TRANSFER_BANNER,
            metadata,
        });
    }
};

export const logStatsigCheckoutTransferBannerOpen = (cart: Cart) => {
    const metadata = {
        ...getProductsMetaData(cart),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_TRANSFER_BANNER_OPEN,
        metadata,
    });
};

export const logStatsigCheckoutTransferAdded = (
    product: TransferProduct,
    trip: TransferTrip | null
) => {
    const metadata = {
        ...getTransferProductMetaData(product),
        ...(trip && getTransferTripMetaData(trip)),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_TRANSFER_ADDED,
        metadata,
    });
};

export const logStatsigCheckoutTransferInteraction = (
    interactionType:
        | 'Search'
        | 'Select'
        | 'Fees'
        | 'Edit'
        | 'Details'
        | 'ChangeTransfer'
        | 'Image',
    transfer: TransferOption | TransferProduct | null,
    trip: TransferTrip | null
) => {
    const option = transfer && !isTransferProduct(transfer) ? transfer : undefined;
    const product = transfer && isTransferProduct(transfer) ? transfer : undefined;
    const metadata = {
        interactionType,
        ...(option && getTransferOptionMetaData(option)),
        ...(product && getTransferProductMetaData(product)),
        ...(trip && getTransferTripMetaData(trip)),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_TRANSFER_INTERACTION,
        metadata,
    });
};

export const logStatsigCheckoutTransferAvailableOptions = (options: TransferOption[]) => {
    const metadata = {
        ...getTransferOptionsMetaData(options),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_TRANSFER_AVAILABLE_OPTIONS,
        metadata,
    });
};

export const logStatsigCheckoutTransferAvailableTrips = (trips: TransferTrip[]) => {
    const metadata = {
        ...getTransferTripsMetaData(trips),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_TRANSFER_AVAILABLE_TRIPS,
        metadata,
    });
};

export const logStatsigCheckoutTicketPolicyOpen = (type: string) => {
    const metadata = {
        type,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_TICKET_POLICY_OPEN,
        metadata,
    });
};

export const logStatsigCheckoutDiscountCodeOpen = () => {
    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_DISCOUNT_CODE_OPEN,
    });
};

export const logStatsigCheckoutDiscountCodeApplied = (code: string, amount: number) => {
    const metadata = {
        discount_code: code,
        discount_amount: amount,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_DISCOUNT_CODE_APPLIED,
        metadata,
    });
};

export const logStatsigCheckoutDiscountCodeError = (code: string) => {
    const metadata = {
        discount_code: code,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_DISCOUNT_CODE_ERROR,
        metadata,
    });
};

export const logStatsigCheckoutCopyFeatureClick = (type: ProductType, cart: Cart) => {
    const metadata = {
        copy_product_type: type,
        ...getProductsMetaData(cart),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_COPY_FEATURE_CLICK,
        metadata,
    });
};

export const logStatsigCheckoutCopyFeatureInvalidForm = (type: ProductType, cart: Cart) => {
    const metadata = {
        copy_product_type: type,
        ...getProductsMetaData(cart),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_COPY_FEATURE_INVALID_FORM,
        metadata,
    });
};

export const logStatsigCheckoutCopyFeatureApplied = (type: ProductType, cart: Cart) => {
    const metadata = {
        copy_product_type: type,
        ...getProductsMetaData(cart),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_COPY_FEATURE_APPLIED,
        metadata,
    });
};

export const logStatsigCheckoutSembonusAvailable = (
    count: number,
    amount: Money,
    cart: Cart
) => {
    if (!eventIsTracked(StatsigEvent.CHECKOUT_SEMBONUS_AVAILABLE)) {
        const metadata = {
            sembonus_currency: amount.Currency,
            sembonus_total_amount: amount.Amount,
            sembonus_count: count,
            ...getProductsMetaData(cart),
        };

        pushStatsig({
            eventName: StatsigEvent.CHECKOUT_SEMBONUS_AVAILABLE,
            metadata,
        });
    }
};

export const logStatsigCheckoutSembonusApplied = (
    count: number,
    amount: Money,
    cart: Cart
) => {
    const metadata = {
        sembonus_currency: amount.Currency,
        sembonus_total_amount: amount.Amount,
        sembonus_count: count,
        ...getProductsMetaData(cart),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_SEMBONUS_APPLIED,
        metadata,
    });
};

export const logStatsigCheckoutSembonusView = (view: SembonusView, cart: Cart) => {
    const metadata = {
        sembonus_view: view,
        ...getProductsMetaData(cart),
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_SEMBONUS_VIEW,
        metadata,
    });
};

export const logStatsigCheckoutSembonusInteraction = (
    interactionType: 'ReadMore' | 'SignUp' | 'Login' | 'OptIn'
) => {
    const metadata = {
        interactionType,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_SEMBONUS_INTERACTION,
        metadata,
    });
};

export const logStatsigCheckoutServicePackageWarningOpen = () => {
    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_SERVICE_PACKAGE_WARNING_OPEN,
    });
};

export const logStatsigCheckoutServicePackageWarningInteraction = (
    interaction: 'yes' | 'no'
) => {
    const metadata = {
        service_package_warning_user_action: interaction,
    };
    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_SERVICE_PACKAGE_WARNING_INTERACTION,
        metadata,
    });
};

export const logStatsigCheckoutBaggageWarningOpen = (position: BaggageWarningPosition) => {
    const metadata = {
        baggage_warning_position: position,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_BAGGAGE_WARNING_OPEN,
        metadata,
    });
};

export const logStatsigCheckoutDuplicatePaxWarningOpen = (count: number) => {
    const metadata = {
        count_duplicate_pax: count,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_DUPLICATE_PAX_WARNING_OPEN,
        metadata,
    });
};

export const logStatsigCheckoutBaggageWarningInteraction = (
    position: BaggageWarningPosition,
    interaction: BaggageWarningInteraction
) => {
    const metadata = {
        baggage_warning_position: position,
        baggage_warning_user_action: interaction,
    };
    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_BAGGAGE_WARNING_INTERACTION,
        metadata,
    });
};

export const logStatsigCheckoutAppliedDiscountInteraction = (
    interactionType: 'MoreInfo' | 'FieldChange' | 'OnToggle',
    code?: string
) => {
    const metadata = {
        interactionType,
        applied_deal_code: code,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_APPLIED_DISCOUNT_INTERACTION,
        metadata,
    });
};

const getProductsMetaData = (cart: Cart) => {
    const { Products: anyProducts } = cart;
    const products = anyProducts.map((p) => p.$type);

    const activities: ActivityProduct[] = getActivityProducts(anyProducts);
    const hotels: HotelProduct[] = getHotelProducts(anyProducts);
    const flights = getFlightProducts(anyProducts);

    return {
        is_only_product: getIsOnlyProduct(cart),
        count_products_applied_discount: getProductsHasAppliedDiscount(anyProducts),
        products,
        ...getProductsType(anyProducts),
        ...getProductsTickets(activities),
        ...getProductsRooms(hotels),
        ...getProductsRyanair(flights),
        ...getBaggageCount(flights),
        ...getSeatingCount(flights),
        // Remove this later when statsig is updated, we should use the count object above
        ...getProductCountByType(anyProducts),
    };
};

const getTransferTripTypes = (trip: TransferTrip) => {
    return `${trip.from.productType}-${trip.to.productType}`;
};

const getTransferTripsMetaData = (trips: TransferTrip[]) => {
    return {
        transfer_trips_count: trips.length,
        transfer_trips_types: trips.map((trip) => getTransferTripTypes(trip)),
    };
};

const getTransferTripMetaData = (trip: TransferTrip) => {
    return {
        transfer_trip_types: getTransferTripTypes(trip),
        transfer_trip_from: trip.from.description,
        transfer_trip_to: trip.to.description,
        transfer_trip_search_flow: trip.searchFlow,
        transfer_trip_adults: trip.request.passengers.adults,
        transfer_trip_children: trip.request.passengers.children,
        transfer_trip_infants: trip.request.passengers.infants,
    };
};

const getTransferOptionsMetaData = (options: TransferOption[]) => {
    return {
        transfer_options_count: options.length,
        transfer_options_partners: uniq(options.map((option) => option.TransferPartner.Name)),
        transfer_options_vehicle_category_types: uniq(
            options.map((option) => option.TransferVehicle.CategoryType)
        ),
        transfer_options_vehicle_service_types: uniq(
            options.map((option) => option.TransferVehicle.ServiceType)
        ),
        transfer_options_gratuity_included_count: options.filter(
            (option) => option.GratuityIncluded
        ).length,
        transfer_options_tolls_included_count: options.filter(
            (option) => option.TollsIncluded
        ).length,
    };
};

const getTransferOptionMetaData = (option: TransferOption) => {
    return {
        transfer_option_price: option.Price.Amount,
        transfer_option_price_currency: option.Price.Currency,
        transfer_option_baggage: option.MaxLargeLuggage,
        transfer_option_distance: option.Distance,
        transfer_option_duration: option.Duration,
        transfer_option_gratuity_included: option.GratuityIncluded,
        transfer_option_tolls_included: option.TollsIncluded,
        transfer_option_seats_adults: option.OptionSeats.Adults,
        transfer_option_seats_children: option.OptionSeats.Children,
        transfer_option_seats_infants: option.OptionSeats.Infants,
        transfer_option_vehicle_category_type: option.TransferVehicle.CategoryType,
        transfer_option_vehicle_service_type: option.TransferVehicle.ServiceType,
        transfer_option_vehicle_model: option.TransferVehicle.Model,
        transfer_option_partner: option.TransferPartner.Name,
    };
};

const getTransferProductMetaData = (product: TransferProduct) => {
    return {
        transfer_product_price: product.Price.Amount,
        transfer_product_price_currency: product.Price.Currency,
        transfer_product_baggage: product.Luggage,
        transfer_product_distance: product.Distance,
        transfer_product_duration: product.Duration,
        transfer_product_gratuity_included: product.GratuityIncluded,
        transfer_product_tolls_included: product.TollsIncluded,
        transfer_product_adults: product.Passengers.Adults,
        transfer_product_children: product.Passengers.Children,
        transfer_product_infants: product.Passengers.Infants,
        transfer_product_vehicle_category_type: product.Vehicle.TransferCategory,
        transfer_product_vehicle_service_type: product.Vehicle.ServiceType,
        transfer_product_vehicle_model: product.Vehicle.Model,
        transfer_product_partner: product.Partner.Name,
    };
};

const getBaggageCount = (products: FlightProduct[]) => {
    const count = sum(products.map(getBaggageSelectedCount));
    return { count_flight_baggage: count };
};

const getSeatingCount = (products: FlightProduct[]) => {
    const count = products.map(getAllSeatsForFlightProduct).flat().length;
    return { count_flight_seats: count };
};

const getProductsHasAppliedDiscount = (products: AnyProduct[]) => {
    return products.filter(
        (x) =>
            x.AppliedDiscount &&
            x.AppliedDiscount.Name &&
            x.AppliedDiscount.Amount?.Amount > 0
    ).length;
};

const getActivityProducts = (products: AnyProduct[]) => {
    return products.filter((x): x is ActivityProduct => x.Type === ProductType.Activity);
};

const getHotelProducts = (products: AnyProduct[]) => {
    return products.filter((x): x is HotelProduct => x.Type === ProductType.Hotel);
};

const getFlightProducts = (products: AnyProduct[]): FlightProduct[] => {
    return products.filter((x): x is FlightProduct => x.Type === ProductType.Flight);
};

const getIsOnlyProduct = (cart: Cart) => {
    if (isFlightOnly(cart)) {
        return ProductType.Flight;
    } else if (isHotelOnly(cart)) {
        return ProductType.Hotel;
    } else if (isFerryOnly(cart)) {
        return ProductType.Ferry;
    } else if (isActivityOnly(cart)) {
        return ProductType.Activity;
    }
    return undefined;
};
export const logStatsigCheckoutCartSessionUpdate = (
    cart: Cart,
    canApply: boolean,
    appliedFields: string[]
) => {
    if (!eventIsTracked(StatsigEvent.CHECKOUT_CART_SESSION_UPDATE)) {
        const metadata = {
            ...getProductsMetaData(cart),
            cart_session_update_can_apply: canApply,
            cart_session_update_is_applied: appliedFields.length > 0,
            cart_session_update_applied_fields: appliedFields,
        };

        pushStatsig({
            eventName: StatsigEvent.CHECKOUT_CART_SESSION_UPDATE,
            metadata,
        });
    }
};

export const logStatsigCheckoutCartSessionStore = (cart: Cart, success: boolean) => {
    if (!eventIsTracked(StatsigEvent.CHECKOUT_CART_SESSION_STORE)) {
        const metadata = {
            ...getProductsMetaData(cart),
            cart_session_store_status: success ? 'success' : 'failure',
        };

        pushStatsig({
            eventName: StatsigEvent.CHECKOUT_CART_SESSION_STORE,
            metadata,
        });
    }
};

export const logStatsigCheckoutServicePackageInteraction = (
    action: 'ShowMore' | 'ReadMore',
    name: ServicePackageName
) => {
    const metadata = {
        service_package: name,
        action: action,
    };

    pushStatsig({
        eventName: StatsigEvent.CHECKOUT_SERVICE_PACKAGE_INTERACTION,
        metadata,
    });
};
